import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Theme preference. `system` follows the OS `prefers-color-scheme`.
 * The actual applied appearance is exposed separately as `resolvedTheme`.
 */
export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** The user's stored preference. */
  theme: ThemePreference;
  /** The appearance actually applied right now ('light' | 'dark'). */
  resolvedTheme: ResolvedTheme;
  /** Set an explicit preference. */
  setTheme: (theme: ThemePreference) => void;
  /** Flip between light and dark (resolves `system` to its current value first). */
  toggleTheme: () => void;
}

const STORAGE_KEY = 'samadhi-theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const prefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const readStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system'
    ? stored
    : 'light';
};

const resolve = (preference: ThemePreference): ResolvedTheme =>
  preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;

/**
 * Applies the resolved theme to <html> by toggling the `.dark` class. The
 * `theme-transition` class enables a one-shot colour cross-fade (see
 * index.css) and is removed after the transition so hovers stay instant.
 */
const applyTheme = (resolved: ResolvedTheme, animate: boolean): void => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  if (animate && !reduceMotion) {
    root.classList.add('theme-transition');
    window.setTimeout(() => root.classList.remove('theme-transition'), 320);
  }

  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredPreference);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolve(readStoredPreference())
  );

  // Apply on mount and whenever the preference changes.
  useEffect(() => {
    const resolved = resolve(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved, true);
  }, [theme]);

  // Track OS changes while the preference is `system`.
  useEffect(() => {
    if (theme !== 'system') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const resolved: ResolvedTheme = media.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyTheme(resolved, true);
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference) => {
    window.localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: ThemePreference = resolve(current) === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, toggleTheme }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
